import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { useAuthStore } from '../../lib/auth';

interface SettingsLayoutProps {
  children: ReactNode;
}

export default function SettingsLayout({ children }: SettingsLayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const { workspace } = useAuthStore();

  // Build settings menu with workspace slug
  const settingsMenu = workspaceSlug ? [
    {
      path: `/${workspaceSlug}/settings`,
      label: t('settings.menu.account', 'Account'),
    },
    {
      path: `/${workspaceSlug}/settings/knowledge`,
      label: t('settings.menu.knowledge', 'Knowledge Taxonomy'),
    },
    {
      path: `/${workspaceSlug}/settings/brand`,
      label: t('settings.menu.brand', 'Visual Brand Guide'),
    },
    {
      path: `/${workspaceSlug}/settings/content`,
      label: t('settings.menu.content', 'Preferred Content'),
    },
    {
      path: `/${workspaceSlug}/settings/api`,
      label: t('settings.menu.api', 'API Connections'),
    },
    {
      path: `/${workspaceSlug}/settings/company`,
      label: t('settings.menu.company', 'Company Settings'),
    },
    // Workspace Settings - Only for Owner
    ...(workspace && workspace.role === 'owner' ? [{
      path: `/${workspaceSlug}/settings/workspace`,
      label: t('settings.menu.workspace', 'Workspace Settings'),
    }] : []),
    {
      path: `/${workspaceSlug}/settings/billing`,
      label: t('settings.menu.billing', 'Billing'),
    },
  ] : [];

  const isActive = (path: string) => {
    if (path.endsWith('/settings')) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex gap-8 p-4 sm:p-6 lg:p-8">
      {/* Side Menu */}
      <aside className="w-64 flex-shrink-0">
        <div className="sticky top-8">
          <h2 className="text-2xl font-bold mb-6 dark:text-white">
            {t('settings.title')}
          </h2>
          <nav className="space-y-1">
            {settingsMenu.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  block px-4 py-3 rounded-lg
                  border-2 transition-all duration-200
                  ${
                    isActive(item.path)
                      ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-800 font-bold text-black dark:text-white'
                      : 'border-transparent hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-700 dark:text-gray-300'
                  }
                `}
              >
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
      </aside>

      {/* Content Area */}
      <main className="flex-1 min-w-0">
        {children}
      </main>
    </div>
  );
}
